/**
 * Non-blocking session expiry warning.
 *
 * Shows a fixed-position banner with a countdown when the JWT is about
 * to expire, giving the user time to save work before auto-logout.
 */

import { useSessionMonitor } from '@/hooks/useSessionMonitor'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Clock } from 'lucide-react'

export function SessionExpiryWarning() {
  const { showExpiryWarning, timeUntilExpiry, dismissWarning } = useSessionMonitor()

  if (!showExpiryWarning || timeUntilExpiry === null) return null

  const minutes = Math.floor(timeUntilExpiry / 60000)
  const seconds = Math.floor((timeUntilExpiry % 60000) / 1000)
  const timeDisplay = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`

  return (
    <div className="fixed top-14 right-4 z-[100] max-w-sm animate-in fade-in slide-in-from-top-2 duration-300">
      <Alert variant="warning" className="shadow-lg">
        <Clock className="h-4 w-4" />
        <AlertTitle>Session Expiring</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>
            Your session will expire in <strong>{timeDisplay}</strong>.
            You will be redirected to login.
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={dismissWarning}
            className="flex-shrink-0"
          >
            Dismiss
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  )
}
